import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Target, Eye, ArrowRight,
  Radar, Bug, KeyRound, Fingerprint, ShieldOff, Laptop,
  Activity, Wrench, Siren, ScrollText, Database, Users,
  Server, BadgeCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { SITE, breadcrumb, orgRef, personRef } from '../components/SEO';

// Each experience area gets a unique icon + vibrant gradient tile so it reads
// as a distinct discipline at a glance — instead of a plain list of words.
const experienceAreas = [
  { name: 'Security Operations',            icon: Radar,       gradient: 'from-cyan-500 to-blue-600' },
  { name: 'Vulnerability Management',       icon: Bug,         gradient: 'from-amber-500 to-orange-600' },
  { name: 'Privileged Access Management',   icon: KeyRound,    gradient: 'from-violet-500 to-fuchsia-600' },
  { name: 'Identity & Access Management',   icon: Fingerprint, gradient: 'from-sky-500 to-cyan-600' },
  { name: 'Data Loss Prevention',           icon: ShieldOff,   gradient: 'from-rose-500 to-red-600' },
  { name: 'Endpoint Security',              icon: Laptop,      gradient: 'from-emerald-500 to-teal-600' },
  { name: 'SIEM & Security Monitoring',     icon: Activity,    gradient: 'from-indigo-500 to-blue-600' },
  { name: 'Security Engineering',           icon: Wrench,      gradient: 'from-blue-500 to-cyan-600' },
  { name: 'Incident Response',              icon: Siren,       gradient: 'from-red-500 to-rose-600' },
  { name: 'Governance, Risk & Compliance',  icon: ScrollText,  gradient: 'from-yellow-500 to-amber-600' },
  { name: 'Data Protection',                icon: Database,    gradient: 'from-teal-500 to-cyan-600' },
  { name: 'Security Awareness',             icon: Users,       gradient: 'from-purple-500 to-indigo-600' },
];

const frameworkExperience = [
  { name: 'ISO/IEC 27001',                     short: 'ISO 27001', from: '#0ea5e9', to: '#2563eb', focus: 'Implementation · Auditing · Compliance' },
  { name: 'PCI DSS',                            short: 'PCI DSS',   from: '#10b981', to: '#0d9488', focus: 'Implementation · Compliance · Controls' },
  { name: 'NIST Cybersecurity Framework 2.0', short: 'NIST CSF',  from: '#f97316', to: '#dc2626', focus: 'Govern · Identify · Protect · Detect · Respond · Recover' },
  { name: 'Security Governance',                short: 'GOV',       from: '#a855f7', to: '#6366f1', focus: 'Policies · Roles · Oversight' },
  { name: 'Data Protection & Privacy',          short: 'DATA',      from: '#22d3ee', to: '#0891b2', focus: 'Confidentiality · Integrity · Availability' },
  { name: 'Risk Management',                    short: 'RISK',      from: '#f59e0b', to: '#b45309', focus: 'Identify · Assess · Treat · Monitor' },
  { name: 'Security Controls',                  short: 'CTRL',      from: '#3b82f6', to: '#1d4ed8', focus: 'Preventive · Detective · Corrective' },
];

const techExperience = [
  { category: 'PAM',                       items: ['CyberArk', 'BeyondTrust', 'Delinea'],     icon: KeyRound,    gradient: 'from-violet-500 to-fuchsia-600' },
  { category: 'Vulnerability Management',  items: ['Rapid7', 'Qualys', 'Nessus'],             icon: Bug,         gradient: 'from-amber-500 to-orange-600' },
  { category: 'EDR / Endpoint',            items: ['CrowdStrike'],                            icon: Laptop,      gradient: 'from-emerald-500 to-teal-600' },
  { category: 'SIEM / Monitoring',         items: ['Splunk', 'Microsoft Sentinel'],           icon: Activity,    gradient: 'from-indigo-500 to-blue-600' },
  { category: 'Data Protection',           items: ['Microsoft Purview'],                      icon: Database,    gradient: 'from-teal-500 to-cyan-600' },
  { category: 'Cloud',                     items: ['Azure', 'AWS'],                           icon: Server,      gradient: 'from-sky-500 to-cyan-600' },
];

const faqs = [
  { q: 'What type of businesses do you help?', a: 'Small and growing businesses that need practical cybersecurity guidance, and organizations that need additional cybersecurity expertise alongside an existing IT team.' },
  { q: 'What does a cybersecurity assessment involve?', a: 'A structured review of your environment, current security controls, key risks and business priorities — followed by a prioritized set of practical improvements.' },
  { q: 'Do small businesses really need cybersecurity services?', a: 'Yes. Smaller businesses are frequent targets precisely because attackers assume defenses are lighter. Practical controls make a measurable difference.' },
  { q: 'How often should vulnerabilities be assessed?', a: 'Continuously, in some form. The right cadence depends on your environment, exposure and business impact — that\'s part of what we help define.' },
  { q: 'Why is MFA important?', a: 'MFA is one of the highest-impact, lowest-cost controls a business can put in place. It significantly reduces the risk of stolen passwords becoming full account takeovers.' },
  { q: 'What is least privilege?', a: 'It means giving each user and system only the access they truly need to do their job — no more. It limits the damage of a compromised account.' },
  { q: 'How should a business protect its backups?', a: 'Have them, isolate them, protect them from being modified or deleted by attackers, and — critically — test that you can actually restore from them.' },
  { q: 'What happens if an employee falls for phishing?', a: 'The impact depends on your controls: MFA, endpoint protection, monitoring and a clear incident reporting path can turn a click into a contained event instead of a breach.' },
  { q: 'How does cybersecurity awareness training work?', a: 'Regular, practical, relevant training grounded in real-world examples — not a one-off lecture. The goal is a security-aware culture, not a memorized checklist.' },
  { q: 'Can Kennyvicops work with an existing IT provider?', a: 'Yes. We regularly work alongside internal IT teams and external providers to add security expertise where it\'s needed.' },
  { q: 'Do you provide remote cybersecurity consulting?', a: 'Yes. Most of our engagements can be delivered remotely, with on-site work when it adds value.' },
];

// Reusable framework shield badge for light backgrounds
const FrameworkBadge = ({ item }) => (
  <div
    className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-blue-500/50 hover:shadow-md hover:shadow-slate-900/5 transition-all p-5 sm:p-6 flex items-center gap-4"
    data-testid={`framework-${item.short.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <div
      className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-xl flex items-center justify-center shadow-lg"
      style={{ background: `linear-gradient(135deg, ${item.from}, ${item.to})` }}
    >
      <svg viewBox="0 0 64 64" className="w-9 h-9 sm:w-11 sm:h-11 text-white/95" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M32 4 L58 14 V32 C58 46 46 56 32 60 C18 56 6 46 6 32 V14 Z" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-white text-xs sm:text-sm font-bold tracking-tight text-center px-1 leading-tight">
        {item.short}
      </span>
    </div>
    <div className="min-w-0">
      <div className="text-[#06101F] font-semibold text-sm sm:text-base truncate">{item.name}</div>
      <div className="text-xs sm:text-sm text-[#475467] mt-1">{item.focus}</div>
    </div>
  </div>
);

const EyebrowLight = ({ children }) => (
  <div className="inline-flex items-center gap-2.5 mb-4">
    <span className="w-6 h-px bg-blue-600/70" />
    <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">{children}</span>
  </div>
);

const AboutPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <SEO
        path="/about"
        title="About Kennyvicops | Cybersecurity Made Practical"
        description="Kennyvicops Cyber Solutions Inc. is founded by Kenny Vic. Vision, mission, cybersecurity experience, framework and compliance experience, and frequently asked questions."
        image="/kenny-vic.webp"
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'About', path: '/about' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            url: `${SITE}/about`,
            mainEntity: personRef,
            publisher: orgRef,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        ]}
      />

      {/* HERO — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(22,135,255,0.16),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-16 sm:pb-20">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <span className="w-6 h-px bg-cyan-300/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">About Kennyvicops</span>
          </div>
          <h1 className="font-editorial text-4xl sm:text-6xl lg:text-[4.25rem] font-semibold leading-[1.05] tracking-tight text-white">
            Cybersecurity made practical.
          </h1>
        </div>
      </section>

      {/* VISION & MISSION — white */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24 grid gap-6 md:grid-cols-2">
          <div className="p-8 sm:p-10 bg-[#F5F8FC] border border-slate-200 rounded-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="font-editorial text-xl sm:text-2xl font-semibold text-[#06101F]">Our Vision</h2>
            </div>
            <p className="text-[#475467] text-base sm:text-lg leading-relaxed">
              To make practical cybersecurity accessible to small and growing businesses, helping them operate securely, confidently and resiliently in a digital world.
            </p>
          </div>
          <div className="p-8 sm:p-10 bg-[#F5F8FC] border border-slate-200 rounded-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="font-editorial text-xl sm:text-2xl font-semibold text-[#06101F]">Our Mission</h2>
            </div>
            <p className="text-[#475467] text-base sm:text-lg leading-relaxed">
              To help businesses understand their cybersecurity risks and take practical action to protect their people, systems and data through security assessments, risk-focused solutions, security services and education.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER — off-white, warm & human */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24 grid gap-10 lg:gap-16 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div className="relative order-2 lg:order-1">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="absolute -inset-3 bg-gradient-to-br from-blue-100 via-cyan-50 to-transparent rounded-3xl" aria-hidden />
              <figure className="relative rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-slate-100 shadow-xl shadow-slate-900/5">
                <img
                  src="/kenny-vic.webp"
                  alt="Kenny Vic — Founder & Principal Cybersecurity Consultant, Kennyvicops"
                  className="w-full h-auto aspect-[4/5] object-cover"
                  data-testid="founder-photo"
                />
              </figure>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 lg:left-auto lg:right-4 lg:translate-x-0 flex items-center gap-2 bg-white shadow-lg shadow-slate-900/10 border border-slate-200 rounded-full px-4 py-2 whitespace-nowrap">
                <BadgeCheck className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-[#101828]">Kenny Vic &middot; Founder</span>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <EyebrowLight>The Founder</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-[#06101F] leading-[1.08]">
              Kenny Vic
            </h2>
            <p className="mt-2 text-blue-700 text-base sm:text-lg font-medium">Founder &amp; Principal Cybersecurity Consultant</p>
            <div className="mt-6 space-y-5 text-[#475467] text-base sm:text-lg leading-relaxed">
              <p>
                Kenny Vic is a cybersecurity professional with extensive hands-on experience across security operations, security engineering, vulnerability management, identity and access management, privileged access management, data protection, endpoint security, security monitoring and governance, risk and compliance.
              </p>
              <p>
                His experience includes practical implementation, assessment, auditing and compliance work across security frameworks and programs, including ISO/IEC 27001 and PCI DSS.
              </p>
              <p>
                Kenny&apos;s approach combines technical security experience with practical communication. Through <span className="text-blue-700 font-semibold">The Cyber Guy</span>, he explains cybersecurity using simple, real-world examples so that business owners, employees and everyday technology users can understand what the risks mean and what they can actually do about them.
              </p>
              <p className="text-[#101828] font-medium">
                Kennyvicops was created to take that practical approach from education into professional cybersecurity services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE AREAS — white */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-2xl mb-12">
            <EyebrowLight>Areas of Cybersecurity Experience</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-[#06101F] leading-[1.08]">
              Practical experience across the disciplines that protect a modern business.
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {experienceAreas.map((a) => (
              <div
                key={a.name}
                data-testid={`experience-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-blue-500/50 hover:shadow-md hover:shadow-slate-900/5 transition-all p-5 sm:p-6"
              >
                <div className={`inline-flex w-14 h-14 items-center justify-center rounded-xl bg-gradient-to-br ${a.gradient} shadow-lg mb-4 group-hover:scale-110 transition-transform`}>
                  <a.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-[#06101F] font-semibold text-base sm:text-lg leading-snug">{a.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRAMEWORK & COMPLIANCE — off-white */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-2xl mb-12">
            <EyebrowLight>Framework &amp; Compliance Experience</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-[#06101F] leading-[1.08]">
              Applied experience with the frameworks that shape business security.
            </h2>
            <p className="mt-5 text-[#475467] text-base sm:text-lg">
              Kennyvicops brings practical experience applying security frameworks, controls and compliance requirements to real-world security environments.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {frameworkExperience.map((f) => (<FrameworkBadge key={f.name} item={f} />))}
          </div>
          <p className="mt-8 text-xs text-slate-500 max-w-2xl">
            &ldquo;Experience&rdquo; is used intentionally. It reflects hands-on work with these frameworks &mdash; not certifications, and not an implication that Kennyvicops is a certification body.
          </p>
        </div>
      </section>

      {/* SECURITY TECHNOLOGY EXPERIENCE — white */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-2xl mb-12">
            <EyebrowLight>Security Technology Experience</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-[#06101F] leading-[1.08]">
              Hands-on work with security technologies businesses actually use.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {techExperience.map((t) => (
              <div
                key={t.category}
                className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-blue-500/50 hover:shadow-md hover:shadow-slate-900/5 transition-all p-6"
                data-testid={`tech-${t.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className={`inline-flex w-11 h-11 items-center justify-center rounded-lg bg-gradient-to-br ${t.gradient} shadow-md`}>
                    <t.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-[#06101F] font-semibold text-base sm:text-lg">{t.category}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {t.items.map((i) => (
                    <span key={i} className="px-3 py-1 text-xs sm:text-sm bg-[#F5F8FC] border border-slate-200 rounded-full text-[#101828] font-medium">
                      {i}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-slate-500 max-w-3xl">
            Technology names listed reflect professional experience with these technologies and do not imply partnership, sponsorship, certification, or endorsement.
          </p>
        </div>
      </section>

      {/* FAQ — off-white */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <EyebrowLight>Frequently Asked Questions</EyebrowLight>
          <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-[#06101F] leading-[1.08] mb-10">
            Straight answers, in plain English.
          </h2>
          <div className="space-y-3">
            {faqs.map((f, idx) => (
              <details
                key={idx}
                className="group bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 open:border-blue-500/40 open:shadow-md open:shadow-slate-900/5 transition-all"
                data-testid={`faq-${idx + 1}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 text-[#06101F] font-semibold">
                  <span>{f.q}</span>
                  <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl leading-none flex-shrink-0">+</span>
                </summary>
                <p className="mt-4 text-sm sm:text-base text-[#475467] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(22,135,255,0.22),transparent_50%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-white leading-[1.1]">
            Ready to talk about your security?
          </h2>
          <p className="mt-5 text-slate-300/95 text-base sm:text-lg max-w-2xl mx-auto">
            Let&apos;s start with a conversation about your environment and priorities.
          </p>
          <div className="mt-8">
            <Link to="/contact">
              <Button
                data-testid="about-cta"
                className="px-8 py-6 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
              >
                Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
